import { useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useRecoilValue } from 'recoil';
import {
  CANVAS_COMPONENT_META,
  COMPONENT_CATEGORIES,
  type ComponentCategoryId,
} from 'config/canvasComponentCatalog';
import { AVAILABLE_CANVAS_COMPONENTS } from 'config/canvas';
import {
  CanvasComponentType,
  CanvasContainerMode,
  CanvasContentId,
} from 'types/canvas';
import useAuth from 'hooks/auth/useAuth';
import { hasInternalDashAccessState, userSubLevelState } from 'states';
import { isValidTokenOverrideTimeframe } from 'util/user';
import { UpsellModal } from 'components/shared/Upsell';
import { SGTooltip } from 'components/core';

interface AddComponentsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selected: CanvasContentId[]) => void;
  currentCount: number;
  maxComponents: number;
  /**
   * Container mode driving copy + multi-vs-single-select UX. Defaults to
   * 'tabbed' so existing callers don't need to opt in.
   */
  mode?: CanvasContainerMode;
  /**
   * Current count of each content id across the whole active workspace.
   * Used to enforce per-type caps (e.g. max 2 Trace tiles per workspace).
   */
  workspaceCountByContentId: Partial<Record<CanvasContentId, number>>;
}

const TYPE_LABEL: Record<CanvasComponentType, string> = {
  chart: 'Chart',
  table: 'Table',
  list: 'List',
  'rich-text': 'Note',
};

export const AddComponentsDialog = ({
  open,
  onClose,
  onConfirm,
  currentCount,
  maxComponents,
  mode = 'tabbed',
  workspaceCountByContentId,
}: AddComponentsDialogProps) => {
  const theme = useTheme();
  const { hasAccessToProduct } = useAuth();
  const userLevel = useRecoilValue(userSubLevelState);
  const isInternalUser = useRecoilValue(hasInternalDashAccessState);
  const [selected, setSelected] = useState<CanvasContentId[]>([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<
    ComponentCategoryId | 'all'
  >('all');
  // Show upsell modal with label when locked component is clicked.
  const [upsellTarget, setUpsellTarget] = useState<{ label: string } | null>(
    null,
  );

  const remaining = Math.max(0, maxComponents - currentCount);
  const remainingAfterSelection = remaining - selected.length;
  const atCapacity = remaining === 0;
  const singleMode = mode === 'single';

  const handleClose = () => {
    setSelected([]);
    setSearch('');
    setActiveCategory('all');
    onClose();
  };

  const handleToggle = (id: CanvasContentId) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter((c) => c !== id);
      }
      if (prev.length >= remaining) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const allComponents = useMemo(
    () =>
      Object.values(AVAILABLE_CANVAS_COMPONENTS).filter(
        (c) =>
          CANVAS_COMPONENT_META[c.contentId]?.internalOnly !== true ||
          isInternalUser,
      ),
    [isInternalUser],
  );

  const visibleCategories = useMemo(
    () =>
      COMPONENT_CATEGORIES.filter((cat) =>
        allComponents.some(
          (c) => CANVAS_COMPONENT_META[c.contentId]?.category === cat.id,
        ),
      ),
    [allComponents],
  );

  const groupedByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allComponents.filter((c) => {
      const meta = CANVAS_COMPONENT_META[c.contentId];
      if (activeCategory !== 'all' && meta.category !== activeCategory) {
        return false;
      }
      if (q.length === 0) {
        return true;
      }
      return c.label.toLowerCase().includes(q);
    });

    return visibleCategories
      .map((cat) => ({
        category: cat,
        items: filtered.filter(
          (c) => CANVAS_COMPONENT_META[c.contentId]?.category === cat.id,
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [allComponents, visibleCategories, search, activeCategory]);

  const totalFiltered = groupedByCategory.reduce(
    (sum, g) => sum + g.items.length,
    0,
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundImage: 'none',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            maxHeight: '85vh',
          },
        }}
      >
        {/* Compact header — single row */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2,
            px: 3,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 600, flex: 1 }}>
            {singleMode ? 'Pick a Component' : 'Add Components'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor:
                remainingAfterSelection < 0
                  ? alpha(theme.palette.error.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.1),
              color:
                remainingAfterSelection < 0
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: 11, lineHeight: 1.4 }}
            >
              {singleMode &&
                (selected.length > 0 ? '1 selected' : 'Single component')}
              {!singleMode && (
                <SGTooltip
                  title={`Only ${maxComponents} components are allowed to be selected`}
                >
                  <>
                    {currentCount + selected.length >= maxComponents
                      ? 'Component limit reached'
                      : `Up to ${maxComponents} components allowed in a container`}
                  </>
                </SGTooltip>
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              ml: 0.5,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers={false}
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 480,
            gap: 4,
          }}
        >
          {/* Filters strip */}
          <Box
            sx={{
              px: 3,
              pt: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            }}
          >
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search components"
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <SearchIcon
                      sx={{ fontSize: 16, color: theme.palette.text.secondary }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontSize: 13,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.8),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1,
                  },
                },
                '& .MuiInputBase-input': {
                  py: 0.75,
                },
              }}
            />

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 2,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              <Chip
                label="All"
                size="small"
                onClick={() => setActiveCategory('all')}
                sx={{
                  height: 24,
                  fontSize: 12,
                  fontWeight: 500,
                  flexShrink: 0,
                  borderRadius: 0.75,
                  ...(activeCategory === 'all'
                    ? {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.18,
                        ),
                        color: theme.palette.primary.main,
                      }
                    : {
                        backgroundColor: 'transparent',
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.08,
                          ),
                        },
                      }),
                }}
              />
              {COMPONENT_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isActive = activeCategory === cat.id;
                return (
                  <Chip
                    key={cat.id}
                    size="small"
                    icon={<Icon sx={{ fontSize: '13px !important' }} />}
                    label={cat.label}
                    onClick={() => setActiveCategory(cat.id)}
                    sx={{
                      height: 24,
                      fontSize: 12,
                      fontWeight: 500,
                      flexShrink: 0,
                      borderRadius: 0.75,
                      '& .MuiChip-icon': {
                        color: 'inherit',
                      },
                      '& .MuiChip-label': {
                        pl: 0.75,
                        pr: 1,
                      },
                      ...(isActive
                        ? {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.18,
                            ),
                            color: theme.palette.primary.main,
                          }
                        : {
                            backgroundColor: 'transparent',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08,
                              ),
                            },
                          }),
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Component list */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 3,
              py: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {atCapacity ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {singleMode
                    ? 'This container already has its one component.'
                    : 'This container is full.'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}
                >
                  {singleMode
                    ? 'Remove the existing component to pick a different one.'
                    : 'Remove a component to add new ones.'}
                </Typography>
              </Box>
            ) : totalFiltered === 0 ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                }}
              >
                <Typography variant="body2">
                  No components match your filters.
                </Typography>
              </Box>
            ) : (
              groupedByCategory.map((group) => {
                const Icon = group.category.icon;
                return (
                  <Stack key={group.category.id} sx={{ gap: 3 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        gap: 2,
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 14,
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {group.category.label}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          height: '1px',
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.15,
                          ),
                          ml: 0.5,
                        }}
                      />
                    </Stack>

                    <Grid container spacing={3}>
                      {group.items.map((c) => {
                        const isSelected = selected.includes(c.contentId);
                        const cantSelectMore =
                          !isSelected && selected.length >= remaining;
                        const meta = CANVAS_COMPONENT_META[c.contentId];
                        // Gate on the category's product, or a per-component
                        // override if set; a locked card opens the upsell.
                        const access = meta?.access;
                        const requiredProduct =
                          access != null
                            ? (access.productType ?? null)
                            : group.category.productType;
                        const subLevelOk =
                          access?.minSubLevelExclusive == null ||
                          userLevel >= access.minSubLevelExclusive ||
                          isValidTokenOverrideTimeframe();
                        const locked =
                          !hasAccessToProduct(requiredProduct) || !subLevelOk;
                        const upsellLabel =
                          access?.upsellLabel ?? group.category.label;
                        // Per-type cap (e.g. max 2 Trace per workspace).
                        const cap = meta?.maxPerWorkspace;
                        const existingOfType =
                          workspaceCountByContentId[c.contentId] ?? 0;
                        const selectedOfType = selected.filter(
                          (id) => id === c.contentId,
                        ).length;
                        const atTypeCap =
                          cap != null &&
                          !isSelected &&
                          existingOfType + selectedOfType >= cap;
                        const disabled =
                          !locked && (cantSelectMore || atTypeCap);
                        const capTooltip = atTypeCap
                          ? `Limit reached: max ${cap} ${c.label} per workspace`
                          : '';
                        const cardEl = (
                          <Box
                            onClick={() => {
                              if (locked) {
                                setUpsellTarget({ label: upsellLabel });
                                return;
                              }
                              if (disabled) {
                                return;
                              }
                              handleToggle(c.contentId);
                            }}
                            sx={{
                              position: 'relative',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                              px: 3,
                              py: 2,
                              width: '100%',
                              borderRadius: 1.25,
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              backgroundColor: isSelected
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.primary.main, 0.035),
                              border: `1px solid ${
                                isSelected
                                  ? theme.palette.primary.main
                                  : alpha(theme.palette.primary.main, 0.18)
                              }`,
                              opacity: disabled ? 0.4 : locked ? 0.7 : 1,
                              transition:
                                'background-color 0.12s ease, border-color 0.12s ease',
                              '&:hover': disabled
                                ? {}
                                : {
                                    borderColor: isSelected
                                      ? theme.palette.primary.main
                                      : alpha(theme.palette.primary.main, 0.5),
                                    backgroundColor: isSelected
                                      ? alpha(theme.palette.primary.main, 0.16)
                                      : alpha(theme.palette.primary.main, 0.07),
                                  },
                            }}
                          >
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                }}
                              >
                                <CheckRoundedIcon sx={{ fontSize: 12 }} />
                              </Box>
                            )}
                            {locked && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: 12,
                                  transform: 'translateY(-50%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  backgroundColor: alpha(
                                    theme.palette.text.secondary,
                                    0.2,
                                  ),
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                <LockOutlinedIcon sx={{ fontSize: 12 }} />
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: '8px',
                                minWidth: 0,
                                pr: isSelected || locked ? '24px' : 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                }}
                              >
                                {c.label}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  flexShrink: 0,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: 0.5,
                                  textTransform: 'uppercase',
                                  color: alpha(
                                    theme.palette.primary.main,
                                    0.85,
                                  ),
                                  px: 1,
                                  borderRadius: 0.5,
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12,
                                  ),
                                  lineHeight: 1.4,
                                }}
                              >
                                {TYPE_LABEL[c.type]}
                              </Box>
                              {cap != null && (
                                <Box
                                  component="span"
                                  sx={{
                                    flexShrink: 0,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    px: 0.75,
                                    borderRadius: 0.5,
                                    lineHeight: 1.4,
                                    color: atTypeCap
                                      ? theme.palette.error.main
                                      : theme.palette.text.secondary,
                                    backgroundColor: atTypeCap
                                      ? alpha(theme.palette.error.main, 0.12)
                                      : alpha(
                                          theme.palette.text.secondary,
                                          0.12,
                                        ),
                                  }}
                                >
                                  {existingOfType + selectedOfType}/{cap}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                        return (
                          <Grid key={c.contentId} item xs={12} sm={6} md={4}>
                            {capTooltip ? (
                              <Tooltip title={capTooltip} placement="top" arrow>
                                <Box sx={{ width: '100%' }}>{cardEl}</Box>
                              </Tooltip>
                            ) : (
                              cardEl
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Stack>
                );
              })
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 1.75,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={0.75}>
            <Button
              onClick={handleClose}
              size="small"
              sx={{ textTransform: 'none', fontSize: 13 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              size="small"
              disabled={selected.length === 0}
              onClick={() => {
                onConfirm(selected);
                setSelected([]);
                setSearch('');
                setActiveCategory('all');
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                px: 1.5,
              }}
            >
              {singleMode
                ? selected.length === 0
                  ? 'Pick'
                  : 'Add component'
                : selected.length === 0
                  ? 'Add'
                  : `Add ${selected.length} component${
                      selected.length === 1 ? '' : 's'
                    }`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <UpsellModal
        open={upsellTarget != null}
        setOpen={(o) => {
          if (!o) {
            setUpsellTarget(null);
          }
        }}
        title={`Unlock ${upsellTarget?.label ?? ''}`}
        subtitle={
          upsellTarget != null
            ? `Subscribe to access ${upsellTarget.label} components on Canvas.`
            : ''
        }
        items={
          upsellTarget != null
            ? [
                `Add every ${upsellTarget.label} chart and table to your Canvas`,
                'Real-time updates that match the source page',
                'Symbol-sharing groups across linked components',
              ]
            : []
        }
      />
    </>
  );
};

export default AddComponentsDialog;
